import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { ExportButton } from '@/app/ExportButton';
import { formatMoney, formatDate } from '@/lib/money';
import type { Transaction, TransactionListResponse, ReferenceData } from './types';
import { TransactionForm } from './TransactionForm';
import { SettleDialog } from './SettleDialog';

/**
 * Filtros LOCAIS desta tela.
 *
 * O intervalo de datas saiu daqui: agora vem do seletor global da barra
 * superior, e vale para o sistema inteiro. O que sobra são os filtros que só
 * fazem sentido em uma lista de lançamentos.
 */
type Filters = {
  type?: 'receita' | 'despesa';
  overdue?: boolean;
  q?: string;
};

export function TransactionsPage({
  selectedIds,
  onOpenAccounts,
  onOpenCards,
  onOpenDebts,
}: {
  selectedIds: number[];
  onOpenAccounts?: () => void;
  /** Leva para Cartões, já na fatura escolhida. */
  onOpenCards?: (cardId: number, invoiceId: number) => void;
  /** Leva para Dívidas e CET. */
  onOpenDebts?: () => void;
}) {
  const { period } = usePeriod();
  const [filters, setFilters] = useState<Filters>({});
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [settling, setSettling] = useState<Transaction | null>(null);

  const queryClient = useQueryClient();

  /**
   * Trocar o período volta para a primeira página.
   *
   * Sem isto, quem está na página 3 de agosto e escolhe um mês com meia dúzia
   * de lançamentos cai numa página vazia e conclui que o mês não tem nada.
   */
  useEffect(() => {
    setPage(1);
  }, [period.from, period.to]);

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000, // muda pouco
  });

  const params = new URLSearchParams({
    from: period.from,
    to: period.to,
    page: String(page),
    per_page: '50',
  });
  if (filters.type) params.set('type', filters.type);
  if (filters.overdue) params.set('overdue', '1');
  if (filters.q) params.set('q', filters.q);

  const list = useQuery({
    queryKey: ['transactions', params.toString(), selectedIds],
    queryFn: () =>
      api.get<TransactionListResponse>(`/transactions?${params}`, { entityIds: selectedIds }),
  });

  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.delete(`/transactions/${id}`, {
        entityIds: selectedIds,
        body: reason ? { reason } : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const totals = list.data?.meta.totals;
  const cardSummaries = list.data?.meta.card_summaries ?? [];
  const loanSummaries = list.data?.meta.loan_summaries ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Lançamentos</h1>

        <div className="flex items-center gap-2">
          {/**
           * OS MESMOS FILTROS DA LISTA.
           *
           * `page` e `per_page` ficam de fora de propósito: o arquivo leva o
           * recorte inteiro, não a página que está aberta. Exportar só as 50
           * linhas visíveis seria a armadilha silenciosa desta tela.
           */}
          <ExportButton
            path="/transactions/export"
            query={{
              from: period.from,
              to: period.to,
              type: filters.type,
              overdue: filters.overdue ? '1' : undefined,
              q: filters.q,
            }}
            entityIds={selectedIds}
            disabled={list.isPending}
          />

          <button
            onClick={() => setFormOpen(true)}
            className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            + Novo lançamento
          </button>
        </div>
      </div>

      {/* ---------- Filtros ---------- */}
      <div className="mt-4 flex flex-wrap items-end gap-2.5 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
        <PeriodBadge />
        <Field label="Tipo">
          <select
            value={filters.type ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setFilters((f) => ({ ...f, type: v === '' ? undefined : (v as 'receita' | 'despesa') }));
              setPage(1);
            }}
            className="input"
          >
            <option value="">Todos</option>
            <option value="receita">Receitas</option>
            <option value="despesa">Despesas</option>
          </select>
        </Field>
        <Field label="Buscar">
          <input
            type="search"
            placeholder="descrição ou documento"
            defaultValue={filters.q ?? ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFilters((f) => ({ ...f, q: (e.target as HTMLInputElement).value || undefined }));
                setPage(1);
              }
            }}
            className="input w-52"
          />
        </Field>
        <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={filters.overdue ?? false}
            onChange={(e) => { setFilters((f) => ({ ...f, overdue: e.target.checked || undefined })); setPage(1); }}
            className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
          />
          Só vencidos
        </label>
        <button
          onClick={() => { setFilters({}); setPage(1); }}
          className="ml-auto pb-2 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
        >
          Limpar
        </button>
      </div>

      {/* ---------- Totais do recorte ---------- */}
      {totals && (
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <Total label="Receitas" value={totals.income} tone="receita" />
          <Total label="Despesas" value={totals.expense} tone="despesa" />
          <Total label="Resultado" value={totals.result} signed />
          <Total label="Em aberto" value={totals.open} />
          <span className="ml-auto self-center text-xs text-slate-400">
            {list.data?.meta.total ?? 0} lançamento(s) no período
          </span>
        </div>
      )}

      {/*
        ================================================================
        AS COMPRAS DO CARTÃO, RESUMIDAS
        ================================================================

        Uma linha por FATURA, e não uma por compra. Numa base real, 33 compras
        de um mês viravam 33 linhas no meio das contas — a lista deixava de
        servir para achar qualquer outra coisa.

        Fica num bloco próprio, ACIMA da lista, e não misturado nas linhas. O
        motivo é a paginação: uma linha sintética dentro de uma lista de 50 em
        50 apareceria repetida em toda página, ou sumiria da segunda, e a
        contagem de registros deixaria de fechar. Aqui ela aparece uma vez.

        O detalhe de cada compra fica em Cartões — que é onde ele já morava.
      */}
      {cardSummaries.length > 0 && (
        <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 dark:border-indigo-900/50 dark:bg-indigo-950/25">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
            Compras no cartão
          </p>

          <div className="mt-2 space-y-1.5">
            {cardSummaries.map((c) => (
              <div
                key={c.invoice_id}
                className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-sm dark:bg-slate-900/50"
              >
                <span className="font-medium">
                  {c.card_name}
                  {c.last4 && <span className="text-slate-400"> ····{c.last4}</span>}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  fatura {c.reference} · {c.purchases} compra{c.purchases > 1 ? 's' : ''} · vence{' '}
                  {formatDate(c.due_date)}
                </span>

                {/**
                  * O ESTADO DA FATURA, aqui mesmo.
                  *
                  * Sem isto, pagar a fatura não mudava nada nesta tela: as
                  * compras são baixadas de verdade, mas elas não aparecem na
                  * lista — este bloco é a única representação do cartão aqui, e
                  * ele mostrava o mesmo antes e depois do pagamento.
                  *
                  * O valor à direita continua sendo o das compras do recorte; a
                  * marca de paga fala da FATURA, que é o que se paga.
                  */}
                {c.is_fully_paid ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    fatura paga
                  </span>
                ) : c.paid_amount !== '0.0000' ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    falta {formatMoney(c.remaining_amount)}
                  </span>
                ) : null}

                <span
                  className={`ml-auto font-medium tabular-nums ${
                    c.is_fully_paid ? 'text-slate-400 dark:text-slate-500' : ''
                  }`}
                >
                  {formatMoney(c.total)}
                </span>

                {/*
                  "Meu editar leva para os Detalhes do cartão correspondente."

                  É lá que cada compra tem editar e excluir — e é lá que ela já
                  estava. Duplicar essas ações aqui criaria dois caminhos para a
                  mesma operação, que acabariam divergindo.
                */}
                <button
                  onClick={() => onOpenCards?.(c.card_id, c.invoice_id)}
                  className="rounded-lg border border-indigo-300 px-2.5 py-1 text-xs transition hover:bg-white dark:border-indigo-800 dark:hover:bg-slate-900"
                >
                  Detalhar
                </button>
              </div>
            ))}
          </div>

          <p className="mt-2 text-xs text-indigo-800/70 dark:text-indigo-300/70">
            O valor já está somado nos totais acima. Cada compra fica em Cartões, onde dá para
            editar e excluir.
          </p>
        </div>
      )}

      {/*
        ================================================================
        AS PARCELAS DE DÍVIDA DO MÊS
        ================================================================

        "Tenho um cartão Itaú pago e o mesmo não aparece em lançamentos."

        Não era cartão: era o consórcio CARRO CRETA, credor Itaú, taxa zero.
        Saíram R$ 741,26 da conta e esta tela não dizia uma palavra — porque,
        por RN-61, só os juros de uma parcela viram despesa, e num consórcio
        sem juros não nasce lançamento nenhum.

        O registro estava certo. O que faltava era CONTAR: dinheiro que sai da
        conta precisa aparecer no mês em que saiu, mesmo não sendo gasto.

        POR QUE UM BLOCO, E NÃO UMA LINHA. Virar linha faria o total do mês
        somar o principal como despesa, e um consórcio de 86 parcelas inflaria
        o resultado em mais de R$ 63 mil ao longo do contrato. O bloco mostra
        sem mentir sobre o que é.
      */}
      {loanSummaries.length > 0 && (
        <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 dark:border-violet-900/50 dark:bg-violet-950/25">
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            Parcelas de dívida
          </p>

          <div className="mt-2 space-y-1.5">
            {loanSummaries.map((p) => (
              <div
                key={p.installment_id}
                className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-sm dark:bg-slate-900/50"
              >
                <span className="font-medium">{p.description}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {p.creditor_name && <>{p.creditor_name} · </>}
                  parcela {p.installment_number} de {p.installments_total} · vence{' '}
                  {formatDate(p.due_date)}
                </span>

                {p.is_paid ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    paga{p.paid_at && <> em {formatDate(p.paid_at)}</>}
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    em aberto
                  </span>
                )}

                {/**
                  * O JURO, SEPARADO — e é isso que impede a conta dobrada.
                  *
                  * O lançamento de juros já está na lista e já está nos totais
                  * do topo. Só o resto da parcela é que não passa por lá.
                  *
                  * Some quando é zero: num consórcio, escrever "R$ 0,00 de
                  * juros" em toda linha é ruído.
                  */}
                {p.interest !== '0.0000' && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    juros {formatMoney(p.interest)}
                  </span>
                )}

                <span
                  className={`ml-auto font-medium tabular-nums ${
                    p.is_paid ? 'text-slate-400 dark:text-slate-500' : ''
                  }`}
                >
                  {formatMoney(p.total)}
                </span>

                <button
                  onClick={() => onOpenDebts?.()}
                  className="rounded-lg border border-violet-300 px-2.5 py-1 text-xs transition hover:bg-white dark:border-violet-800 dark:hover:bg-slate-900"
                >
                  Detalhar
                </button>
              </div>
            ))}
          </div>

          {/*
            O AVISO É O OPOSTO DO AVISO DO CARTÃO, e de propósito.

            No cartão, o valor JÁ está nos totais. Aqui, só os juros estão: o
            principal sai da conta e abate a dívida, sem passar pelo resultado.
            Quem lê os dois blocos seguidos precisa dessa diferença dita.
          */}
          <p className="mt-2 text-xs text-violet-800/70 dark:text-violet-300/70">
            Só os juros entram nos totais acima. O resto da parcela sai da conta e abate a dívida —
            não é despesa. O cronograma completo fica em Dívidas e CET.
          </p>
        </div>
      )}

      {/* ---------- Tabela ---------- */}
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {list.isPending ? (
          <TableSkeleton />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data.data.length === 0 ? (
          <EmptyState onCreate={() => setFormOpen(true)} />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5 font-medium">Vencimento</th>
                {/* A data em que o dinheiro saiu, ao lado da em que devia sair.
                    São perguntas diferentes, e quem confere o extrato do banco
                    precisa da segunda. */}
                <th className="px-3 py-2.5 font-medium">Baixa</th>
                <th className="px-3 py-2.5 font-medium">Descrição</th>
                <th className="px-3 py-2.5 font-medium">Categoria</th>
                <th className="px-3 py-2.5 font-medium">Entidade</th>
                <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                <th className="px-3 py-2.5 font-medium">Situação</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.data.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="whitespace-nowrap px-3 py-2.5">
                    {formatDate(t.due_date)}
                    {t.is_overdue && (
                      <span className="ml-1.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                        vencido
                      </span>
                    )}
                  </td>

                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-500 dark:text-slate-400">
                    {t.settled_at ? (
                      formatDate(t.settled_at)
                    ) : (
                      /* Um traço, e não vazio: célula em branco é indistinguível
                         de dado que não carregou. */
                      <span className="text-slate-300 dark:text-slate-600">—</span>
                    )}
                  </td>

                  <td className="px-3 py-2.5">
                    <span className="font-medium">{t.description}</span>
                    {t.installment_total && t.installment_total > 1 && (
                      <span className="ml-1.5 text-xs text-slate-400">
                        {t.installment_number}/{t.installment_total}
                      </span>
                    )}
                    {t.is_card_purchase && (
                      <span className="ml-1.5 text-xs text-slate-400">· cartão</span>
                    )}

                    {/* Cliente/fornecedor abaixo da descrição, e não em coluna
                        própria: a tabela já tem seis colunas, e "CONTABILIDADE
                        FOCUS" costuma ser a descrição E o fornecedor. Coluna
                        nova repetiria o mesmo texto na maioria das linhas. */}
                    {t.party_name && (
                      <span className="block text-xs text-slate-400">{t.party_name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
                    {t.category_name ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
                    {t.entity_name ?? '—'}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-2.5 text-right font-medium ${
                      t.type === 'receita'
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-rose-700 dark:text-rose-400'
                    }`}
                  >
                    {/* Sinal + rótulo: cor nunca é o único indicador (RNF-26) */}
                    {t.type === 'receita' ? '+' : '−'} {formatMoney(t.net_amount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <StatusBadge status={t.status} label={t.status_label} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    {t.remaining_amount !== '0.0000' && !t.is_card_purchase && (
                      <button
                        onClick={() => setSettling(t)}
                        className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
                      >
                        Dar baixa
                      </button>
                    )}
                    {/* Editar vem antes de excluir: quase todo "excluir e
                        lançar de novo" era, na verdade, uma correção. */}
                    <button
                      onClick={() => setEditing(t)}
                      className="ml-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => {
                        const hasSettlement = t.settled_amount !== '0.0000';
                        const reason = hasSettlement
                          ? window.prompt('Este lançamento tem baixa. Informe o motivo da exclusão:')
                          : window.confirm(`Excluir "${t.description}"?`)
                            ? ''
                            : null;
                        if (reason === null) return;
                        remove.mutate({ id: t.id, reason: reason || undefined });
                      }}
                      className="ml-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                      title="Excluir"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------- Paginação ---------- */}
      {list.data && list.data.meta.last_page > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">
            Página {list.data.meta.current_page} de {list.data.meta.last_page}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-700"
            >
              Anterior
            </button>
            <button
              disabled={page >= list.data.meta.last_page}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-700"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {(formOpen || editing) && reference.data && (
        <TransactionForm
          /* `key` remonta o formulário ao trocar de lançamento: sem ele, abrir
             o segundo reaproveitaria os `useState` do primeiro — a tela
             mostraria um lançamento e salvaria no outro. */
          key={editing?.id ?? 'novo'}
          transaction={editing}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={(warnings) => {
            setFormOpen(false);
            setEditing(null);
            // Os avisos descrevem efeitos A DISTANCIA — fatura recalculada,
            // ocorrência desligada da série. Nenhum deles aparece nesta tela.
            if (warnings?.length) window.alert(warnings.join('\n\n'));
            refreshAll();
          }}
        />
      )}

      {settling && reference.data && (
        <SettleDialog
          transaction={settling}
          accounts={reference.data.accounts}
          cards={reference.data.cards}
          transactionEntityId={settling.entity_id}
          selectedIds={selectedIds}
          onOpenAccounts={onOpenAccounts}
          onClose={() => setSettling(null)}
          onSettled={() => { setSettling(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function Total({
  label,
  value,
  tone,
  signed,
}: {
  label: string;
  value: string;
  tone?: 'receita' | 'despesa';
  signed?: boolean;
}) {
  const color = signed
    ? value.startsWith('-')
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-emerald-600 dark:text-emerald-400'
    : tone === 'receita'
      ? 'text-emerald-700 dark:text-emerald-400'
      : tone === 'despesa'
        ? 'text-rose-700 dark:text-rose-400'
        : '';

  return (
    <span>
      <span className="text-slate-500 dark:text-slate-400">{label}: </span>
      <strong className={color}>{formatMoney(value)}</strong>
    </span>
  );
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  const styles: Record<string, string> = {
    pago: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    recebido: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    parcialmente_pago: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    cancelado: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    previsto: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };

  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        styles[status] ?? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300'
      }`}
    >
      {label}
    </span>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse p-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="mb-2 h-8 rounded bg-slate-100 dark:bg-slate-800" />
      ))}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="p-10 text-center">
      <p className="font-medium">Nenhum lançamento no período</p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajuste os filtros ou registre o primeiro.
      </p>
      <button
        onClick={onCreate}
        className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
      >
        + Novo lançamento
      </button>
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="p-10 text-center">
      <p className="font-medium text-rose-700 dark:text-rose-400">Não foi possível carregar.</p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {error instanceof ApiError ? error.message : 'Erro desconhecido.'}
      </p>
      <button
        onClick={onRetry}
        className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-slate-700"
      >
        Tentar de novo
      </button>
    </div>
  );
}
